import {
  useCallback,
  useId,
  useRef,
  type ChangeEventHandler,
  type InputHTMLAttributes,
} from 'react';
import { IconClipboard, IconPhoto, IconUpload } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { Tooltip } from './Tooltip.js';
import { WP_IMAGE_FILE_ACCEPT } from './FilePickerField.js';
import { useClipboardImagePaste } from '../utils/useClipboardImagePaste.js';
import styles from './GalleryImagePickerPanel.module.css';

export type GalleryImagePickerItem = {
  id: string;
  url: string | null;
  label?: string;
  disabled?: boolean;
  disabledReason?: string;
};

export type GalleryImagePickerPanelProps = {
  className?: string;
  items: GalleryImagePickerItem[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  onUpload: (file: File) => void;
  uploadLabel?: string;
  pasteLabel?: string;
  emptyLabel?: string;
  /** Narrow grid for thumbnail popover. */
  compact?: boolean;
  disabled?: boolean;
  accept?: string;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'accept' | 'onChange'>;
};

export function GalleryImagePickerPanel({
  className,
  items,
  selectedId = null,
  onSelect,
  onUpload,
  uploadLabel = 'Téléverser une nouvelle image',
  pasteLabel = 'Coller une image',
  emptyLabel = 'Aucune image dans la galerie. Téléversez ou collez une image pour commencer.',
  compact = false,
  disabled = false,
  accept = WP_IMAGE_FILE_ACCEPT,
  inputProps,
}: GalleryImagePickerPanelProps) {
  const autoId = useId();
  const inputId = `wp-gallery-upload-${autoId}`;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleUploadChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.currentTarget.files?.[0] ?? null;
    e.currentTarget.value = '';
    if (file) onUpload(file);
  };

  const { wrapProps, pasteFromClipboard } = useClipboardImagePaste({
    disabled,
    accept,
    onFile: onUpload,
  });

  const renderTile = (item: GalleryImagePickerItem) => {
    const isSelected = selectedId === item.id;
    const isDisabled = disabled || Boolean(item.disabled);
    const tile = (
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        className={cx(
          styles.tile,
          isSelected && styles.tileSelected,
          item.disabled && styles.tileDisabled,
        )}
        onClick={() => {
          if (isDisabled) return;
          onSelect(item.id);
        }}
      >
        <span className={styles.tileMediaShell}>
          {item.url ? (
            <img src={item.url} alt="" className={styles.tileMedia} />
          ) : (
            <span className={styles.tilePlaceholder} aria-hidden>
              <IconPhoto size={compact ? 16 : 20} stroke={1.25} />
            </span>
          )}
        </span>
        <span className={styles.tileLabel} title={item.label}>
          {item.label ?? '\u00a0'}
        </span>
      </button>
    );

    const shell = (
      <div key={item.id} className={styles.tileShell}>
        {tile}
      </div>
    );

    if (item.disabled && item.disabledReason) {
      return (
        <Tooltip key={item.id} label={item.disabledReason} position="bottom" portal>
          {shell}
        </Tooltip>
      );
    }

    return shell;
  };

  return (
    <div className={cx(styles.panel, compact && styles.panelCompact, className)} {...wrapProps}>
      <div
        className={cx(styles.gridScroll, !compact && styles.gridScrollExpanded)}
        role="listbox"
        aria-label="Galerie"
      >
        {items.length === 0 ? (
          <p className={styles.empty}>{emptyLabel}</p>
        ) : (
          <div className={cx(styles.grid, compact && styles.gridCompact)}>{items.map(renderTile)}</div>
        )}
      </div>
      <div className={styles.divider} role="separator" />
      <input
        {...inputProps}
        ref={inputRef}
        id={inputId}
        type="file"
        className={styles.visuallyHidden}
        accept={accept}
        disabled={disabled}
        aria-label={uploadLabel}
        onChange={handleUploadChange}
      />
      <button type="button" className={styles.uploadRow} disabled={disabled} onClick={openFilePicker}>
        <span className={styles.uploadIcon} aria-hidden>
          <IconUpload size={16} stroke={1.5} />
        </span>
        <span className={styles.uploadCopy}>
          <span>{uploadLabel}</span>
          <span className={styles.uploadHint}>ou coller (Ctrl+V)</span>
        </span>
      </button>
      <button
        type="button"
        className={cx(styles.uploadRow, styles.pasteRow)}
        disabled={disabled}
        onClick={() => void pasteFromClipboard()}
      >
        <span className={styles.uploadIcon} aria-hidden>
          <IconClipboard size={16} stroke={1.5} />
        </span>
        {pasteLabel}
      </button>
    </div>
  );
}
